import { Alert, Button, CircularProgress, Grid, IconButton, Snackbar as MSnackbar } from "@mui/material";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import Close from "@mui/icons-material/Close";
import { css } from "@emotion/css";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const SnackContext = /*#__PURE__*/ createContext({ error: input => { }, loading: message => { }, success: message => { }, hide: () => { }, showAction: (input, actions) => { } });
export function useSnackbar() { return useContext(SnackContext); }
function newItem(type, message, actions) { return { type, message, actions }; }
const minTime = 100;
const minErrorTime = 5 * 1000;
function since(tm) { return Date.now() - tm; }
export function Snackbar(props) {
    const [severity, setSeverity] = useState("info");
    const [message, setMessage] = useState(null);
    const [loading, setLoading] = useState(false);
    const tLast = useRef(0);
    useEffect(() => { if (loading)
        return; let tm = setTimeout(() => { setMessage(null); }, props.timeout); return () => clearTimeout(tm); }, [loading, message, severity, props.timeout]);
    const queue = useRef([]);
    const [hasQueued, setHasQueued] = useState(false);
    const [actions, setActions] = useState([]);
    const processItem = useCallback(item => { tLast.current = Date.now(); loadingRef.current = false; switch (item.type) {
        case "error":
            setSeverity("error");
            severityRef.current = "error";
            setLoading(false);
            setMessage(item.message || "Unknown Error");
            setActions(item.actions || []);
            break;
        case "loading":
            setSeverity("info");
            severityRef.current = "info";
            loadingRef.current = true;
            setLoading(true);
            setMessage(item.message || "Loading...");
            setActions(item.actions || []);
            break;
        case "success":
            severityRef.current = "success";
            setSeverity("success");
            setLoading(false);
            setMessage(item.message || "Success");
            setActions(item.actions || []);
            break;
        case "hide":
            severityRef.current = null;
            setLoading(false);
            setMessage(null);
            break;
    } }, []);
    useEffect(() => {
        if (!hasQueued)
            return;
        const i = setInterval(() => {
            for (let i = 0; i < 1000; i++) {
                if (severityRef.current === "error" && since(tLast.current) < minErrorTime) {
                    return; // wait for next
                }
                const item = queue.current.shift();
                if (!item) {
                    setHasQueued(false);
                    return;
                }
                if (queue.current.length > 1 && item.type === "loading") {
                    continue;
                }
                processItem(item);
                return;
            }
        }, minTime);
        return () => clearInterval(i);
    }, [hasQueued, processItem]);
    const loadingRef = useRef(false);
    loadingRef.current = loading;
    const severityRef = useRef(message === null ? null : severity);
    severityRef.current = message === null ? null : severity;
    const ctx = useMemo(() => {
        function handle(item) {
            let timeoutPassed = true; // no timeout for most items
            if (severityRef.current === "error" && since(tLast.current) < minErrorTime) {
                timeoutPassed = false;
            }
            const enoughTimePassed = timeoutPassed || loadingRef.current;
            console.log("snack", enoughTimePassed, severityRef.current, since(tLast.current), minErrorTime, item);
            if (enoughTimePassed && queue.current.length === 0) {
                processItem(item);
            }
            else {
                queue.current.push(item);
            } // always start queue, just in case another item comes in
            setHasQueued(true);
        }
        return { error: message => { handle(newItem("error", message)); }, loading: message => { handle(newItem("loading", message)); }, success: message => { handle(newItem("success", message)); }, hide: () => { handle(newItem("hide")); }, showAction: (input, actions) => { handle(newItem("loading", input, actions)); } };
    }, [processItem]);
    return /*#__PURE__*/ _jsxs(SnackContext.Provider, { value: ctx, children: [props.children, /*#__PURE__*/ _jsx(MSnackbar, { open: message !== null, children: /*#__PURE__*/ _jsx(Alert, { variant: "filled", severity: severity, classes: { message: messageCls }, action: loading && actions.length === 0 && /*#__PURE__*/ _jsx("div", { style: { height: "84%", display: "flex", alignItems: "center" }, children: /*#__PURE__*/ _jsx("span", { style: { color: "white", width: "1em", height: "1em" }, children: /*#__PURE__*/ _jsx(CircularProgress, { size: "small", color: "inherit", style: { display: "block" } }) }) }), onClose: !loading ? () => setMessage(null) : undefined, children: /*#__PURE__*/ _jsxs(Grid, { container: true, spacing: 1, alignItems: "center", style: { minWidth: 175 }, children: [/*#__PURE__*/ _jsx(Grid, { item: true, xs: true, children: message }), !loading && actions.length === 0 && /*#__PURE__*/ _jsx(Grid, { item: true, children: /*#__PURE__*/ _jsx(IconButton, { style: { color: "white", margin: -10 }, size: "small", onClick: () => setMessage(null), children: /*#__PURE__*/ _jsx(Close, { fontSize: "inherit", color: "inherit" }) }) }), actions.map(a => /*#__PURE__*/ _jsx(Grid, { item: true, children: /*#__PURE__*/ _jsx(Button, { onClick: () => a.callback(), size: "small", color: "secondary", children: a.title }) }, a.title))] }) }) })] });
}
const messageCls = css({ "div&": { overflow: "visible" } });
